import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { loadPdfDocument } from "./utils";
import { renderPageThumbnail } from "./pdfThumbnails";
import { formatMergedSummary } from "./mergeSummary";
import { markBlobForTrial, wouldMark } from "@/lib/trialMark";
import { useUsageSnapshot } from "@/lib/usageGate";

type PdfDoc = Awaited<ReturnType<typeof loadPdfDocument>>;

// Rendering hundreds of pages just to show a preview would stall the tab for a result the person
// mostly wants to glance at, so the strip stops at a cap and says so rather than silently
// truncating. (The larger viewer can still page through every page: it renders on demand.)
const MAX_THUMBNAILS = 60;

/**
 * A scrollable strip of every page of a finished PDF, with the real page count and file size above
 * it -- so the person can confirm the pages and their order *before* downloading. Each thumbnail
 * opens a larger view of that page (QA MP-12: a separate "Preview first page" button duplicated
 * what the strip already showed, and only for page one).
 */
export function PdfPageStrip({ blob }: { blob: Blob }) {
  const [pageCount, setPageCount] = useState<number | null>(null);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  const [openPage, setOpenPage] = useState<number | null>(null);
  const pdfRef = useRef<PdfDoc | null>(null);
  // The size of the file the person will actually get: a trial download carries the small FreeDailyPro badge,
  // which adds a few KB, so the label is measured on that marked copy (marking is deterministic and idempotent).
  const [deliveredSize, setDeliveredSize] = useState<number | null>(null);
  const usage = useUsageSnapshot(); // the trial/paid state arrives after the first render
  useEffect(() => {
    let cancelled = false;
    setDeliveredSize(null);
    if (!wouldMark("application/pdf", "merged.pdf")) return;
    void markBlobForTrial(blob, "merged.pdf").then((b) => { if (!cancelled) setDeliveredSize(b.size); });
    return () => { cancelled = true; };
  }, [blob, usage]);

  useEffect(() => {
    let cancelled = false;
    let pdf: PdfDoc | null = null;
    setPageCount(null);
    setThumbnails([]);
    setFailed(false);
    setOpenPage(null);
    (async () => {
      try {
        pdf = await loadPdfDocument(new File([blob], "merged.pdf", { type: "application/pdf" }));
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
        const shown = Math.min(pdf.numPages, MAX_THUMBNAILS);
        for (let n = 1; n <= shown; n++) {
          const url = await renderPageThumbnail(pdf, n, 110);
          if (cancelled) return;
          setThumbnails((prev) => [...prev, url]);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      pdfRef.current = null;
      void pdf?.loadingTask.destroy();
    };
  }, [blob]);

  if (failed) {
    return <p className="text-sm text-muted-foreground">Merged file — page preview isn't available for this file.</p>;
  }

  return (
    <div className="space-y-2" data-testid="merged-page-strip">
      <p className="text-sm font-medium">
        {pageCount === null ? "Preparing preview…" : formatMergedSummary(pageCount, deliveredSize ?? blob.size)}
        {pageCount !== null && pageCount > MAX_THUMBNAILS && (
          <span className="text-muted-foreground font-normal"> — showing the first {MAX_THUMBNAILS} pages</span>
        )}
      </p>
      <div className="flex gap-2 overflow-x-auto pb-2" role="list" aria-label="Pages of the merged PDF">
        {thumbnails.map((src, i) => (
          <figure key={i} role="listitem" className="shrink-0 text-center">
            <button
              type="button"
              onClick={() => setOpenPage(i + 1)}
              aria-label={`Open page ${i + 1} larger`}
              title="Click to enlarge"
              className="block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <img src={src} alt={`Page ${i + 1}`} className="rounded border bg-white transition-shadow hover:shadow-md cursor-zoom-in" style={{ width: 110 }} />
            </button>
            <figcaption className="text-xs text-muted-foreground mt-1">{i + 1}</figcaption>
          </figure>
        ))}
      </div>
      {openPage !== null && pageCount !== null && pdfRef.current && (
        <PageViewer pdf={pdfRef.current} pageCount={pageCount} page={openPage} onPage={setOpenPage} onClose={() => setOpenPage(null)} />
      )}
    </div>
  );
}

function PageViewer({ pdf, pageCount, page, onPage, onClose }: { pdf: PdfDoc; pageCount: number; page: number; onPage: (n: number) => void; onClose: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setError(false);
    renderPageThumbnail(pdf, page, 760).then(
      (url) => { if (!cancelled) setSrc(url); },
      () => { if (!cancelled) setError(true); },
    );
    return () => { cancelled = true; };
  }, [pdf, page]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" && page < pageCount) onPage(page + 1);
      if (e.key === "ArrowLeft" && page > 1) onPage(page - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [page, pageCount, onPage]);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Page {page} of {pageCount}</DialogTitle>
          <DialogDescription className="sr-only">Larger view of page {page}. Use the arrow keys or the buttons to move between pages.</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-[200px] items-center justify-center">
          {error ? (
            <p className="text-sm text-muted-foreground">This page couldn't be shown.</p>
          ) : src ? (
            <img src={src} alt={`Page ${page} of ${pageCount}`} className="max-h-[70vh] w-auto max-w-full rounded border bg-white" />
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading page" />
          )}
        </div>
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => onPage(page + 1)} disabled={page >= pageCount}>
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
